import { useCallback, useMemo, useState } from 'react'
import { DndContext, DragOverlay, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import { AddToProductProductList } from './AddToProductProductList'
import { AddToProductSourcePanel } from './AddToProductSourcePanel'
import { AddToProductMid } from './AddToProductMid'
import { AddToProductBottomBar } from './media/AddToProductBottomBar'
import { resolveDrop } from './media/resolveDrop'
import { usePendingChannelDrops } from './media/usePendingChannelDrops'
import { useSlotOccupancy } from './media/useSlotOccupancy'
import { buildOccupancySnapshot } from './media/buildOccupancySnapshot'
import type { AddToProductImage, AddToProductPayload, AddToProductTarget } from './types'
import type { ProductSearchMatch } from '@/lib/api/products-search'

interface AddToProductWorkspaceProps {
  payload: AddToProductPayload
  tab: 'content' | 'media'
  selected: ProductSearchMatch | null
  onSelect: (p: ProductSearchMatch | null) => void
  onComplete?: () => void
}

const SLOT_COUNT = 8

/**
 * Live-fix: slot-image drag source path. Returns `true` when the drag was a
 * slot-to-slot interaction (handled or rejected with a toast), so the caller
 * can short-circuit before falling through to the source-panel resolveDrop
 * path. Cross-channel moves are NOT supported — backend has no API for it
 * and the UX surfaces a warning toast instead of silently no-op'ing.
 */
function handleSlotImageDrag(
  event: DragEndEvent,
  pending: ReturnType<typeof usePendingChannelDrops>,
  t: (key: string) => string,
): boolean {
  const aData = event.active?.data?.current as
    | { kind?: string; channelId?: string; slot?: number }
    | undefined
  if (aData?.kind !== 'slot-image') return false
  const oData = event.over?.data?.current as
    | { target?: string; channelId?: string; slot?: number }
    | undefined
  if (oData?.target !== 'channel') return true
  if (aData.channelId !== oData.channelId) {
    toast.warning(t('addToProduct.media.crossChannelMoveNotSupported'))
    return true
  }
  if (typeof aData.slot === 'number' && typeof oData.slot === 'number') {
    pending.reorderSlots(aData.channelId!, aData.slot, oData.slot)
  }
  return true
}

/**
 * Owns the shared DndContext + 3-column grid. Drops are STAGED into pending
 * state (usePendingChannelDrops); the user reviews them in the slot grids
 * (amber border) and commits/discards via the sticky BottomBar. Save runs the
 * two-step (transferImageToProduct → linkAssetToChannel) for channel targets
 * — that path is JTL-visible — and the single gallery-transfer for AI-ref.
 */
export function AddToProductWorkspace({
  payload,
  tab,
  selected,
  onSelect,
  onComplete,
}: AddToProductWorkspaceProps) {
  const { t } = useTranslation()
  const channels = useProductChannelList(selected?.id ?? '')
  const pending = usePendingChannelDrops(selected?.id ?? '', onComplete)
  // Snapshot of saved slot occupancy. Forwarded to pending.add() so the hook
  // can plan insert-with-shift against the live workspace state (Bundle-D).
  // Re-derived whenever the underlying TanStack query data changes.
  const { filledByChannel } = useSlotOccupancy(selected?.id ?? null)
  const snapshot = useMemo(
    () => buildOccupancySnapshot(filledByChannel),
    [filledByChannel],
  )

  // Floating cursor-preview during drag. Needed because the source panel has
  // overflow-hidden, which clips any transform applied to the source itself.
  // DragOverlay is portal'd by dnd-kit above all panels and follows the cursor.
  const [activeImage, setActiveImage] = useState<AddToProductImage | null>(null)
  const handleDragStart = (event: DragStartEvent) => {
    const img = event.active.data.current?.image as AddToProductImage | undefined
    if (img) setActiveImage(img)
  }
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveImage(null)
    if (!selected) return
    if (handleSlotImageDrag(event, pending, t)) return
    const resolved = resolveDrop(event)
    if (resolved) pending.add(resolved.image, resolved.target, snapshot)
  }
  const handleDragCancel = () => setActiveImage(null)

  // Auto-fill stages every payload image into channels[0] free slots
  // sequentially. Pure staging — Save commits everything in one batch.
  const autoFill = useCallback(() => {
    if (!selected) return
    const channelId = channels[0]?.id
    if (!channelId) return
    for (let i = 0; i < Math.min(payload.images.length, SLOT_COUNT); i++) {
      const target: AddToProductTarget = { type: 'channel', channelId, slot: i + 1 }
      pending.add(payload.images[i], target, snapshot)
    }
  }, [selected, channels, payload.images, pending, snapshot])

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex-1 grid grid-cols-[280px_1fr_320px] overflow-hidden">
        <AddToProductProductList
          preselect={payload.preselect}
          selectedProductId={selected?.id ?? null}
          onSelect={onSelect}
        />
        <AddToProductMid
          selected={selected}
          tab={tab}
          payload={payload}
          pendingByChannelSlot={pending.pendingByChannelSlot}
          pendingByAiRefSlot={pending.pendingByAiRefSlot}
          reorderPlans={pending.reorderPlans}
          onDelete={pending.requestDelete}
        />
        <AddToProductSourcePanel
          images={payload.images}
          texts={payload.texts}
          onAutoFill={selected ? autoFill : undefined}
        />
      </div>
      <DragOverlay dropAnimation={null}>
        {activeImage ? (
          <div
            data-testid="atp-drag-overlay"
            className="w-24 h-24 rounded-lg overflow-hidden shadow-2xl ring-2 ring-primary-500 bg-white pointer-events-none"
          >
            <img src={activeImage.url} alt="" className="w-full h-full object-cover" />
          </div>
        ) : null}
      </DragOverlay>
      <AddToProductBottomBar
        hasChanges={pending.hasChanges}
        saving={pending.saving}
        channelCount={pending.affectedChannels.size}
        imageCount={pending.affectedImages.size}
        onSave={pending.save}
        onDiscard={pending.clear}
      />
    </DndContext>
  )
}
